import { CategoryProductSchemaProps } from '@99designs/marketing-ui';
import { Question } from '../components/CategoryPage/FAQSection/FaqSection';
import { getCtaColorConfig } from '../util/getCtaColorConfig';
import type { ButtonColorConfig } from '@99designs/emu';

export const productLandingGroupKeys = [
    'logo-design',
    'brand-identity-pack',
    'logo-brand-guide',
    'logo-business-card-design',
    'social-media-pack',
] as const;

export type ProductLandingGroupKey = typeof productLandingGroupKeys[number];
export type ProductLandingGroupKeys = typeof productLandingGroupKeys;

export type Why99dDesignerInfo = {
    id: string;
    name: string;
    image: string;
};

export type HeroImage = {
    sources: string[];
    altText: string;
    attributions?: {
        id: string;
        name: string;
    };
};

export type ProductGroup = {
    title: string;
    description: string;
    heroTitle: string;
    productSchema?: CategoryProductSchemaProps;
    productDetailsRelativeLink: `/${ProductLandingGroupKey}/details`;
    introduction: string;
    features: string[];
    heroImage: HeroImage;
    heroCtaAppearance?: ButtonColorConfig;
    why99dTitle: string;
    why99dDesignerInfo: Why99dDesignerInfo;
    howItWorksPricing: string;
    howItWorksHeading: string;
    howItWorksContestDescription: string;
    howItWorksPricingOption: string;
    faqHeading: string;
    faqQuestions: Question[];
    breadcrumbLabel: string;
};

export const getProductLandingGroups = (
    productLandingGroupKey: ProductLandingGroupKey
): ProductGroup => {
    const productLandingGroups: Readonly<Record<ProductLandingGroupKey, ProductGroup>> = {
        'logo-design': {
            title: __('Custom Logo Design Services. Professional Logos Online'),
            description: __(
                'Get a professional logo from the #1 choice for logo design. Original designs. Award-winning designers. Real human customer service. Quality guaranteed.'
            ),
            heroTitle: __('Custom logo design made for your business'),
            productSchema: {
                categoryKey: 'logo-design',
                productName: __placeholder('Logo Design'),
                description: __placeholder(
                    "Welcome to 99designs, the #1 trusted source for quality design you'll love. Get your perfect logo design with our expert designers today."
                ),
                ratingValue: '4.8',
                reviewCount: 26043,
            },
            productDetailsRelativeLink: '/logo-design/details',
            heroCtaAppearance: getCtaColorConfig('#3806CA', '#3308AF', '#3806CA', '#FFFFFF'),
            introduction: __(
                'Grow your business with a professional, custom logo designed by our freelance creative experts. Because good design makes great business.'
            ),
            features: [
                __('1 custom logo design'),
                __('Editable, digital, and print-ready files'),
                __('Full copyright ownership'),
            ],
            heroImage: {
                sources: [
                    '/images/product-landing/hero/logo-design/logo-design-hero_0-599.webp',
                    '/images/product-landing/hero/logo-design/logo-design-hero_600-899.webp',
                    '/images/product-landing/hero/logo-design/logo-design-hero_900-1299.webp',
                    '/images/product-landing/hero/logo-design/logo-design-hero_1300-above.webp',
                ],
                altText: '', // Display as presentational
            },
            why99dTitle: __('So, why 99designs for custom logo design?'),
            why99dDesignerInfo: {
                id: '2829526',
                name: 'leonelbalza',
                image: '/images/product-landing/why99d/Why99d_logo-design.png',
            },
            howItWorksPricing: '/logo-design/details',
            howItWorksHeading: __('How to get a logo on 99designs'),
            howItWorksContestDescription: __(
                'Get multiple design concepts from our designer community, provide feedback and then pick your favorite design.'
            ),
            howItWorksPricingOption: __('See logo design pricing options.'),
            faqHeading: __('Your logo design FAQs, answered.'),
            faqQuestions: [
                {
                    id: 'best-way-to-get-company-logo',
                    title: __("What's the best way to get a logo for my company?"),
                    keywords: ['entreprise', '99designs', 'variété'],
                    messages: [
                        {
                            copy: __(
                                'Most businesses create a logo in one of three ways: by running a design contest, hiring a freelance designer or making their own with a logo builder. Each option has advantages and drawbacks, but we think <ContestsLink>design contests are the best option</ContestsLink> to get a custom company logo because they offer you multiple ideas and flexibility. Not sure which way is right for you? <CreateLogoLink>Check out our guide to discover the best way to design your logo.</CreateLogoLink>'
                            ),
                            links: {
                                CreateLogoLink: '/blog/logo-branding/how-to-create-logo/',
                                ContestsLink: '/contests',
                            },
                        },
                    ],
                },
                {
                    id: 'good-business-logo',
                    title: __('What makes a good business logo?'),
                    keywords: ['company'],
                    messages: [
                        {
                            copy: __(
                                'A good company logo does three things: it tells potential customers if the product is right for them; it communicates what you stand for; and it makes them remember your brand.'
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                "The best way to design a logo that communicates this information is to work with a professional logo designer. They'll be able to recommend the right <TypeOfLogoLink>type of logo</TypeOfLogoLink> and suggest <ColorLink>colors</ColorLink>, <ShapesLink>shapes</ShapesLink> and <FontsLink>fonts</FontsLink> that deliver the message you want your brand to send."
                            ),
                            links: {
                                TypeOfLogoLink: '/blog/tips/types-of-logos/',
                                ColorLink: '/blog/tips/logo-color-meanings/',
                                ShapesLink: '/blog/tips/logo-shapes/',
                                FontsLink: '/blog/logo-branding/logo-fonts/',
                            },
                        },
                    ],
                },
                {
                    id: 'design-own-logo',
                    title: __('How can I design my own logo?'),
                    keywords: ['maker'],
                    messages: [
                        {
                            copy: __(
                                "If you're not a trained graphic designer with design software, you can make your own logo using a logo builder or logo design app. Generally, these tools use templates to help you build a basic logo. If you want a custom logo design that sets you apart from the competition, a design contest is a better option. But sometimes, price and speed are your most important factors. If that's the case, check out our list of the best <LogoMakersLink>logo makers</LogoMakersLink> and the best <LogoDesignAppsLink>logo design apps</LogoDesignAppsLink>."
                            ),
                            links: {
                                LogoMakersLink: '/blog/logo-branding/best-logo-makers/',
                                LogoDesignAppsLink: '/blog/resources/logo-design-apps/',
                            },
                        },
                    ],
                },
                {
                    id: 'logo-design-cost',
                    title: __('How much does a logo design cost?'),
                    keywords: ['money', 'cash', 'package', 'pricing'],
                    messages: [
                        {
                            copy: __(
                                'There’s a wide price range for logos, from almost free to {maxLogoPriceInFaqs} or more. On 99designs, you can get a professionally designed logo from {fromPrice}.'
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                'What should your budget be? Read our article on <DesignCostLink>logo design cost</DesignCostLink> to find out what you can expect at each price point.'
                            ),
                            links: {
                                DesignCostLink: '/blog/logo-branding/logo-design-cost/',
                            },
                        },
                    ],
                },
                {
                    id: 'contest-deliverables',
                    title: __('What will I get with a logo design contest?'),
                    keywords: ['deliverable', 'deliverables', 'contents', 'ownership', 'trademark'],
                    messages: [
                        {
                            copy: __(
                                "At the end of the day you'll get one totally custom logo, complete with full legal copyrights for that winning design."
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                "You'll also get all the <ImageFileTypesLink>image files</ImageFileTypesLink> you'll need to use your logo both online and in print. This means you'll get the original, editable vector file (usually an Adobe Illustrator file), vector EPS files in both CMYK and RGB (for print and web, respectively), and a web preview file, like a JPG on PNG, so you can preview your logo on virtually any device."
                            ),
                            links: {
                                ImageFileTypesLink: '/blog/tips/image-file-types/',
                            },
                        },
                        {
                            copy: __(
                                "Plus, you'll get the full legal copyright, so the design will be all yours."
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                'Unfortunately at this time we cannot offer animated logos through logo design contests. If you would like an animated logo, please hire an <AnimatedLogoDesignerLink>animated logo designer</AnimatedLogoDesignerLink> for a freelance project.'
                            ),
                            links: {
                                AnimatedLogoDesignerLink:
                                    '/designers/search?categories=logo-design&q=animation, animated',
                            },
                        },
                    ],
                },
                {
                    id: 'pricing-package',
                    title: __('Which pricing package should I choose for my logo design contest?'),
                    keywords: ['price', 'cost', 'money', 'cash'],
                    messages: [
                        {
                            copy: __(
                                "We guarantee that you'll get a great logo design at any pricing level, but our higher-priced packages give you more design options and better support."
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                "Here's how it works: When you pay more, the prize for the winning designer is bigger, too. Choosing Silver over Bronze means you'll get more submissions to choose from. Our Gold and Platinum contests are restricted, so only hand-selected, Top- and Mid-Level designers can participate, meaning you'll be sure to get a top-quality logo. Finally, Platinum contests offer guidance from a dedicated Support Manager."
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                "Running a logo contest is fun, but can be time-consuming. When selecting your pricing package, think about how much time you'll have to review the amount of submissions and what your knowledge level is in writing briefs and working with designers. If you're still unsure, contact us for a <DesignConsultationLink>design consultation</DesignConsultationLink>."
                            ),
                            links: { DesignConsultationLink: '/design-consultation' },
                        },
                    ],
                },
                {
                    id: 'copyright-ownership',
                    title: __('Who owns what copyright and when?'),
                    keywords: ['legal', 'ownership', 'trademark'],
                    messages: [
                        {
                            copy: __(
                                "On 99designs, designers agree to upload only original artwork, so each designer owns the rights to their design submissions until you choose your contest's winner. During the final stage of a logo design contest, called handover, the designer agrees to transfer the rights, and you become the owner of the copyright of your winning logo design."
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: 'use-own-images',
                    title: __('Can I use my own images in my design?'),
                    keywords: ['assets'],
                    messages: [
                        {
                            copy: __(
                                'Yes. If you have any specific images (like symbols or icons) that need to be included with your logo, please submit them to designers with your brief.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: 'required-preparation',
                    title: __('What do I need to prepare for my logo design contest?'),
                    keywords: ['preparation'],
                    messages: [
                        {
                            copy: __(
                                'Lots of ideas! Get inspiration by searching through <LogoIdeasLink>logo ideas</LogoIdeasLink> created by our design community.'
                            ),
                            links: { LogoIdeasLink: '/inspiration/logos' },
                        },
                        {
                            copy: __(
                                'Bring as many specifics as you can, too. Include designs you like, brand details, <ColorLink>logo colors</ColorLink>, <FontsLink>fonts</FontsLink>, <TypeOfLogoLink>type of logo</TypeOfLogoLink>, style, product photos and any other specifications that would affect your design.'
                            ),
                            links: {
                                LogoIdeasLink: '/inspiration/logos',
                                ColorLink: '/blog/tips/logo-color-meanings/',
                                FontsLink: '/blog/logo-branding/logo-fonts/',
                                TypeOfLogoLink: '/blog/tips/types-of-logos/',
                            },
                        },
                        {
                            copy: __(
                                'The more you give the designers, the better your logo will be!'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: 'confidential-design',
                    title: __('Can I keep my logo designs confidential?'),
                    keywords: ['privacy'],
                    messages: [
                        {
                            copy: __(
                                'Yes. You can choose to "<MakeContestPrivateLink>Make your contest private</MakeContestPrivateLink>" from the Contest options section when you launch your contest. This will prohibit designers from posting their submissions to your contest in portfolios or elsewhere, and your contest will not appear in search results.'
                            ),
                            links: {
                                MakeContestPrivateLink:
                                    'http://support.99designs.com/hc/en/articles/204761515-Can-I-hide-my-contest-from-search-engines',
                            },
                        },
                        {
                            copy: __(
                                'This option will also automatically attach a standard NDA template to your contest. If you prefer, you can also upload your own NDA.'
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                "Please note that if you do not select this option up front, we can't always remove it from search results later."
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: 'money-back-guarantee',
                    title: __('Is there really a 100% money back guarantee?'),
                    keywords: ['refund', 'benefit'],
                    messages: [
                        {
                            copy: __(
                                "Yes. We offer a <MoneyBackGuaranteeLink>100% money-back guarantee</MoneyBackGuaranteeLink> within 60 days of payment on all Logo Design Contests, except those with a <GuaranteedDesignerPrizeLink>guaranteed designer prize</GuaranteedDesignerPrizeLink> and those that have entered the Final Round. You can also call us anytime to help you rewrite your brief, reopen your contest to ensure you get a good result and help you pick a winning design. Remember, if we refund your contest, you aren't legally entitled to use any of the designs submitted to your contest."
                            ),
                            links: {
                                MoneyBackGuaranteeLink:
                                    'http://support.99designs.com/hc/en/articles/204108729-100-Money-back-guarantee-For-real',
                                GuaranteedDesignerPrizeLink:
                                    'http://support.99designs.com/hc/en/articles/204761955-What-is-a-guaranteed-design-contest',
                            },
                        },
                    ],
                },
                {
                    id: 'work-with-single-designer',
                    title: __('Can I work with a single designer to make a logo?'),
                    keywords: ['creator', 'one'],
                    messages: [
                        {
                            copy: __(
                                "We think logo contests are the bees knees, but sometimes it makes sense to work with an individual freelance logo designer. In that case, you can search through our <LogoDesignersPortfoliosLink>logo designers' portfolios</LogoDesignersPortfoliosLink>, or check out our article on the <BestLogoDesignersLink>best logo designers</BestLogoDesignersLink> on 99designs."
                            ),
                            links: {
                                LogoDesignersPortfoliosLink:
                                    '/designers/search?categories=logo-design',
                                BestLogoDesignersLink: '/blog/designers/best-logo-designers/',
                            },
                        },
                    ],
                },
                {
                    id: 'work-with-designer-once-contest-ends',
                    title: __('Can I work with my designer once my contest ends?'),
                    keywords: ['one'],
                    messages: [
                        {
                            copy: __(
                                'Absolutely! Finding a designer with whom you can build a long-term working relationship is the ultimate goal. When your logo design contest finishes, you can continue working directly with your designer on additional designs through <ProjectsLink>1-to-1 Projects</ProjectsLink>.'
                            ),
                            links: { ProjectsLink: '/projects' },
                        },
                    ],
                },
                {
                    id: 'additional-logo-variations',
                    title: __('What if I need additional logo variations?'),
                    keywords: ['variety', 'flexibility'],
                    messages: [
                        {
                            copy: __(
                                'Your logo design contest includes one winning logo. If you need a second logo, you have several options: you can award multiple winners, run a second contest or work directly with your winning designer to create the additional assets.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '99designs-details',
                    title: __('Why choose 99designs over a logo maker or design agency?'),
                    keywords: ['benefit', 'pros'],
                    messages: [
                        {
                            copy: __(
                                "Every business has a different set of needs and that's exactly why these different types of logo design services exist. Your choice boils down to determining what's important for you at this stage of your business."
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                'This guide may help you understand the <LogoBuilderDifferenceLink>difference between logo builders and us</LogoBuilderDifferenceLink>.'
                            ),
                            links: {
                                LogoBuilderDifferenceLink:
                                    '/blog/logo-branding/how-to-create-logo/',
                            },
                        },
                        {
                            copy: __(
                                "If you decide a logo creator is what's best for you right now, here's our list of the <BestLogoMakersLink>best logo makers</BestLogoMakersLink>."
                            ),
                            links: {
                                BestLogoMakersLink: '/blog/logo-branding/best-logo-makers/',
                            },
                        },
                    ],
                },
                {
                    id: 'branding-package',
                    title: __('What if I need more than a logo?'),
                    keywords: ['brand', 'package', 'online'],
                    messages: [
                        {
                            copy: __(
                                'We can help. Save money with one of our bundles! Get a <BusinessCardLink>business card</BusinessCardLink>, a <WebsiteLink>website</WebsiteLink>, or <SocialMediaLink>social media designs</SocialMediaLink> with your logo. Or get a business starter kit with a whole <BrandIdentityLink>Brand Identity Pack</BrandIdentityLink>.'
                            ),
                            links: {
                                BusinessCardLink: '/logo-business-card-design',
                                WebsiteLink: '/logo-website-squarespace',
                                SocialMediaLink: '/social-media-pack',
                                BrandIdentityLink: '/brand-identity-pack',
                            },
                        },
                        {
                            copy: __(
                                'You can also check out our <CategoriesLink>other design categories</CategoriesLink> or <DesignersSearchLink>search for a designer</DesignersSearchLink> that can create the exact materials you need.'
                            ),
                            links: {
                                CategoriesLink: '/categories',
                                DesignersSearchLink: '/designers/search',
                            },
                        },
                    ],
                },
            ],
            breadcrumbLabel: __placeholder('Logo design'),
        },
        'brand-identity-pack': {
            title: __('Branding Package & Brand Identity Design'),
            description: __(
                'Get a professional, custom branding package from the creative experts. Brand identity pack and logo design. Award-winning designers. Original designs.'
            ),
            heroTitle: __('The complete branding package for your business'),
            productSchema: {
                categoryKey: 'brand-identity-pack',
                productName: __placeholder('Brand Identity Pack'),
                description: __placeholder(
                    "Welcome to 99designs, the #1 trusted source for quality design you'll love. Get your perfect logo design with our expert designers today."
                ),
                ratingValue: '4.9',
                reviewCount: 110,
            },
            productDetailsRelativeLink: '/brand-identity-pack/details',
            heroCtaAppearance: getCtaColorConfig('#245FED', '#144DD5', '#245FED', '#FFFFFF'),
            introduction: __(
                'Connect with our creative experts and grow your business with a professional, custom branding package.'
            ),
            features: [
                __('1 custom logo design'),
                __('1 set of stationery designs including business card, letterhead and envelope'),
                __('1 Facebook cover'),
                __('Editable, digital, and print-ready files'),
                __('Full copyright ownership'),
            ],
            heroImage: {
                sources: [
                    '/images/product-landing/hero/logo-brand-identity-pack/logo-brand-identity-pack-hero_0-599.webp',
                    '/images/product-landing/hero/logo-brand-identity-pack/logo-brand-identity-pack-hero_600-899.webp',
                    '/images/product-landing/hero/logo-brand-identity-pack/logo-brand-identity-pack-hero_900-1299.webp',
                    '/images/product-landing/hero/logo-brand-identity-pack/logo-brand-identity-pack-hero_1300-above.webp',
                ],
                altText: '', // Display as presentational
                attributions: {
                    id: '798132',
                    name: 'SFUMATO™',
                },
            },
            why99dTitle: __('So, why 99designs for a brand identity design?'),
            why99dDesignerInfo: {
                id: '798132',
                name: 'SFUMATO™',
                image: '/images/product-landing/why99d/Why99d_brand-identity-pack.png',
            },
            howItWorksPricing: '/brand-identity-pack/details',
            howItWorksHeading: __('How to get a logo and brand identity pack on 99designs'),
            howItWorksContestDescription: __(
                'Get multiple design concepts from our designer community, provide feedback and then pick your favorite design.'
            ),
            howItWorksPricingOption: __('See logo & brand identity pricing options.'),
            faqHeading: __('Your branding FAQs, answered.'),
            faqQuestions: [
                {
                    title: __('What designs will I receive with my brand identity pack?'),
                    messages: [
                        {
                            copy: __(
                                'This branding package includes a custom logo design, a business card design, plus a letterhead, envelope and Facebook cover to show it all off.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    title: __('What do I need to prepare for my design contest?'),
                    messages: [
                        {
                            copy: __(
                                'Lots of ideas! If you’re looking for inspiration for your branding package, <Explore99designs>explore 99designs</Explore99designs> for fantastic examples of logos and brand identity packages created by our design community.'
                            ),
                            links: {
                                Explore99designs: '/discover',
                            },
                        },
                        {
                            copy: __(
                                'Bring as many specifics as you can, too. Include brand details (e.g. <ColorLink>colors</ColorLink>, <FontsLink>fonts</FontsLink>), preferred <TypeOfLogoLink>type of logo</TypeOfLogoLink>, style suggestions, designs you like, information about your business, product photos and any other specifications that would affect your design.'
                            ),
                            links: {
                                TypeOfLogoLink: '/blog/tips/types-of-logos/',
                                ColorLink: '/blog/tips/logo-color-meanings/',
                                FontsLink: '/blog/logo-branding/logo-fonts/',
                            },
                        },
                        {
                            copy: __(
                                'The more you give the designers, the better they can design a logo and brand identity for you!'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    title: __('What if I have need additional logo sizes/types?'),
                    messages: [
                        {
                            copy: __(
                                'Your custom logo and brand identity design contest includes one winning logo and one complete brand identity package. If you need a second logo for different business or product, or if you need multiple versions of your collateral, you have a couple of options: you can run a second contest, or you can start a <ProjectsLink>1-to-1 Project</ProjectsLink> with your winning designer to create the additional assets.'
                            ),
                            links: {
                                ProjectsLink: '/projects',
                            },
                        },
                    ],
                },
                {
                    title: __('Who owns what copyright and when?'),
                    messages: [
                        {
                            copy: __(
                                'On 99designs, designers agree to upload only original artwork, so each designer owns the rights to their design submissions until you choose your contest’s winner. During the design handover stage, the designer agrees to transfer the rights, and you become the owner of the copyright of your winning logo and brand identity design.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    title: __(
                        'Which package should I choose for my custom logo and brand identity pack contest?'
                    ),
                    messages: [
                        {
                            copy: __(
                                'We guarantee that you’ll get a great logo and brand identity design at any pricing level, but our higher-priced packages give you more design options and better support.'
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                "Here’s how it works: When you pay more, the prize for the winning designer is bigger, too. More experienced designers participate in your contest, so you get more high-quality designs to choose from. More options means additional time that's needed to review your designs. That’s why our Gold and Platinum packages offer dedicated managers to help streamline that process by writing creative briefs and giving your designers feedback."
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                "When selecting your logo and brand identity contest package, think about how much time you’ll have and what experience you have writing briefs and working with designers. If you're still unsure, contact us for a design consultation."
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    title: __('Is there really a 100% money back guarantee?'),
                    messages: [
                        {
                            copy: __(
                                'We offer a <MoneyBackGuaranteeLink>100% money-back guarantee</MoneyBackGuaranteeLink> within 60 days of payment on all Logo and Brand Identity Design Contests, except those with a guaranteed prize and those that have entered the Final Round. You can also call us anytime to help you rewrite your brief, reopen your project to ensure you get a good result, and help you pick a winning design. Remember, if we refund your contest, you aren’t legally entitled to use any of the designs submitted to your contest.'
                            ),
                            links: {
                                MoneyBackGuaranteeLink:
                                    'http://support.99designs.com/hc/en/articles/204108729-100-Money-back-guarantee-For-real',
                            },
                        },
                    ],
                },
                {
                    title: __(
                        'What file types will I receive at the end of my logo & brand identity pack contest?'
                    ),
                    messages: [
                        {
                            copy: __(
                                'You’ll get the original, editable vector files (usually an Adobe Illustrator file), vector EPS files in both CMYK and RGB (for print and web, respectively), and web preview files, like a JPG on PNG, so you can preview your designs on virtually any device. Plus, you’ll get the full legal copyright, so the design will be all yours.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    title: __('Can I use my images in my design?'),
                    messages: [
                        {
                            copy: __(
                                'Yes. If you have any specific images that need to be included with your logo or brand identity, please submit them to designers with your brief.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    title: __(
                        'Once my contest is over, can I continue to work with the winning designer?'
                    ),
                    messages: [
                        {
                            copy: __(
                                'Of course! We actually encourage you do so. You can use our <ProjectsLink>1-1 projects</ProjectsLink> product to continue to work with your winning designer on other design projects (for an additional cost).'
                            ),
                            links: {
                                ProjectsLink: '/projects',
                            },
                        },
                    ],
                },
                {
                    title: __('Why do I only see logos in my contest?'),
                    messages: [
                        {
                            copy: __(
                                'To keep the decision-making process as simple as possible, your logo & brand identity pack contest will focus on the logo portion only. Once you’ve picked a winning logo design to keep, that designer will work with you to create the perfect business card, stationery and Facebook cover. It usually takes about a week.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    title: __('How can I protect my design contest with an NDA?'),
                    messages: [
                        {
                            copy: __(
                                'Choose “Make your contest private” from the Contest options section before you launch your logo and brand identity design contest. This will prohibit designers from using their designs elsewhere, and your contest will be unsearchable on the web.'
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                'This will automatically attach a standard NDA template to your contest. If you prefer, you can also upload your own NDA.'
                            ),
                            links: {},
                        },
                    ],
                },
            ],
            breadcrumbLabel: __placeholder('Logo & brand identity pack'),
        },
        'logo-brand-guide': {
            title: __placeholder('Get a logo and brand guide'),
            description: __placeholder('Get a logo and brand guide'),
            heroTitle: __('Get a brand guide and logo for your business'),
            productSchema: {
                categoryKey: 'logo-brand-guide',
                productName: __placeholder('Logo Brand Guild'),
                description: __placeholder(
                    "Welcome to 99designs, the #1 trusted source for quality design you'll love. Get your perfect logo design with our expert designers today."
                ),
                ratingValue: '5',
                reviewCount: 8,
            },
            productDetailsRelativeLink: '/logo-brand-guide/details',
            heroCtaAppearance: getCtaColorConfig('#1F68FF', '#0053FF', '#1F68FF', '#FFFFFF'),
            introduction: __(
                'Connect with our creative experts and grow your business with a logo design and brand guide that includes fonts, colors and styling.'
            ),
            features: [
                __('1 custom logo design'),
                __('1 brand guide document including guidelines for fonts, colors and usage'),
                __('Editable, digital, and print-ready files'),
                __('Full copyright ownership'),
            ],
            heroImage: {
                sources: [
                    '/images/product-landing/hero/logo-brand-guide/logo-brand-guide-hero_0-599.webp',
                    '/images/product-landing/hero/logo-brand-guide/logo-brand-guide-hero_600-899.webp',
                    '/images/product-landing/hero/logo-brand-guide/logo-brand-guide-hero_900-1299.webp',
                    '/images/product-landing/hero/logo-brand-guide/logo-brand-guide-hero_1300-above.webp',
                ],
                altText: '', // Display as presentational
                attributions: {
                    id: '2557917',
                    name: 'GalaxyGhost',
                },
            },
            why99dTitle: __('So, why 99designs for brand guide design?'),
            why99dDesignerInfo: {
                id: '2557917',
                name: 'GalaxyGhost',
                image: '/images/product-landing/why99d/Why99d_logo-brand-guide.png',
            },
            howItWorksPricing: '/logo-brand-guide/details',
            howItWorksHeading: __('How to get a logo and brand guide on 99designs'),
            howItWorksContestDescription: __(
                'Get multiple design concepts from our designer community, provide feedback and then pick your favorite design.'
            ),
            howItWorksPricingOption: __('See logo & brand guide pricing options.'),
            faqHeading: __('Your brand design FAQs, answered.'),
            faqQuestions: [
                {
                    id: '',
                    title: __('What designs will I receive with my logo & brand guide contest?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                "You'll get a custom logo with a brand guide document (PDF) that shows your logos, fonts, colors & usage guidelines."
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('What do I need to prepare for my logo & brand guide contest?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'Lots of ideas! If you’re looking for inspiration, <Explore99designs>explore 99designs</Explore99designs> for fantastic examples of logos and brand identity packages created by our design community.'
                            ),
                            links: {
                                Explore99designs: '/discover',
                            },
                        },
                        {
                            copy: __(
                                'Bring as many specifics as you can, too. Include brand details (e.g. colors, fonts), preferred <TypeOfLogo>type of logo</TypeOfLogo>, style suggestions, designs you like, information about your business, product photos and any other specifications that would affect your design.'
                            ),
                            links: {
                                TypeOfLogo: 'blog/tips/types-of-logos/',
                            },
                        },
                        {
                            copy: __(
                                'The more you give the designers, the better they can design a logo and brand identity for you!'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __(
                        'Once my contest is over, can I continue to work with the winning designer?'
                    ),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'Of course! We actually encourage you do so. You can use our <ProjectsLink>1-1 projects</ProjectsLink> product to continue to work with your winning designer on other design projects (for an additional cost).'
                            ),
                            links: {
                                ProjectsLink: '/projects',
                            },
                        },
                    ],
                },
                {
                    id: '',
                    title: __('Why do I only see logos in my contest?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'To keep the decision-making process as simple as possible, your logo & brand guide contest will focus on the logo portion only. Once you’ve picked a winning logo design to keep, that designer will work with you to create the perfect brand guide. It usually takes about a week.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('How can I protect my design contest with an NDA?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'Choose “Make your contest private” from the Contest options section before you launch your logo and brand guide contest. This will prohibit designers from using their designs elsewhere, and your contest will be unsearchable on the web.'
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                'This will automatically attach a standard NDA template to your contest. If you prefer, you can also upload your own NDA.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('What do I get when I buy a Brand guide?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'An expert designer will create a professional Brand guide in PDF format that you can easily share with your team or partners.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('Can I see an example of what a Brand guide looks like?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'Sure. Check out the Brand guide produced for <FrugallySustainableBrandGuidelines>Frugally Sustainable (PDF).</FrugallySustainableBrandGuidelines>'
                            ),
                            links: {
                                FrugallySustainableBrandGuidelines:
                                    '/attachment/183/download/FrugallySustainableBrandGuidelines',
                            },
                        },
                    ],
                },
                {
                    id: '',
                    title: __(
                        'What file types will I receive at the end of my logo & brand guide contest?'
                    ),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                "You’ll get the original, editable vector files (usually an Adobe Illustrator file), vector EPS files in both CMYK and RGB (for print and web, respectively), and web preview files, like a JPG on PNG, so you can preview your designs on virtually any device. Plus, you'll get the full legal copyright, so the design will be all yours. You'll receive your brand guide as a portable PDF document."
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('What if I need additional logo sizes/types?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'Your custom logo and brand guide contest includes one winning logo and one complete Brand guide document. If you need a second logo for different business or product, you have a couple of options: you can run a second contest, or you can start a 1-to-1 Project with your winning designer to create the additional assets.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('Who owns what copyright and when?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'On 99designs, designers agree to upload only original artwork, so each designer owns the rights to their design submissions until you choose your contest’s winner. During the design handover stage, the designer agrees to transfer the rights, and you become the owner of the copyright of your winning logo and brand guide.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __(
                        'Which package should I choose for my custom logo and brand guide contest?'
                    ),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'We guarantee that you’ll get a great logo and brand guide at any pricing level, but our higher-priced packages give you more design options and better support.'
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                "Here’s how it works: When you pay more, the prize for the winning designer is bigger, too. More experienced designers participate in your contest, so you get more high-quality designs to choose from. More options means additional time that's needed to review your designs. That’s why our Gold and Platinum packages offer dedicated managers to help streamline that process by writing creative briefs and giving your designers feedback."
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                "When selecting your logo and brand guide package, think about how much time you'll have and what experience you have writing briefs and working with designers. If you're still unsure, contact us for a design consultation."
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('Is there really a 100% money back guarantee?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'We offer a <MoneyBackGuaranteeLink>100% money-back guarantee</MoneyBackGuaranteeLink> within 60 days of payment on all Logo and Brand Identity Design Contests, except those with a guaranteed prize and those that have entered the Final Round. You can also call us anytime to help you rewrite your brief, reopen your project to ensure you get a good result, and help you pick a winning design. Remember, if we refund your contest, you aren’t legally entitled to use any of the designs submitted to your contest.'
                            ),
                            links: {
                                MoneyBackGuaranteeLink:
                                    'http://support.99designs.com/hc/en/articles/204108729-100-Money-back-guarantee-For-real',
                            },
                        },
                    ],
                },
                {
                    id: '',
                    title: __("What's included in a Brand guide?"),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                "You'll receive a beautiful front cover, about page, logo usage, typography, color palette, glossary, and back cover. If you have any additional requests, please ask your chosen designer."
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __("What's the turnaround time?"),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'After your logo design contest is complete, your designer will deliver your brand guide in approximately 7 days.'
                            ),
                            links: {},
                        },
                    ],
                },
            ],
            breadcrumbLabel: __placeholder('Logo & brand guide'),
        },
        'logo-business-card-design': {
            title: __('Business Card Logos - Custom Logos For Your Business Card'),
            description: __(
                'Get a professional business card and logo design from the creative experts. Logos for business cards. Business card logo design. Award-winning designers. Quality guaranteed.'
            ),
            heroTitle: __("Business card and logo design you'll love"),
            productSchema: {
                categoryKey: 'logo-business-card-design',
                productName: __placeholder('Logo Business Card Design'),
                description: __placeholder(
                    "Welcome to 99designs, the #1 trusted source for quality design you'll love. Get your perfect logo design with our expert designers today."
                ),
                ratingValue: '4.8',
                reviewCount: 1168,
            },
            productDetailsRelativeLink: '/logo-business-card-design/details',
            heroCtaAppearance: getCtaColorConfig('#FFCE14', '#FFB905', '#FFCE14', '#1C1B1A'),
            features: [
                __('1 custom logo design'),
                __('1 business card design'),
                __('Editable, digital, and print-ready files'),
                __('Full copyright ownership'),
            ],
            introduction: __(
                "Connect with our creative experts and grow your business with professional, custom design. Pair your logo with business cards so good you won't want to give them away!"
            ),
            heroImage: {
                sources: [
                    '/images/product-landing/hero/logo-business-card/logo-business-card-hero_0-599.webp',
                    '/images/product-landing/hero/logo-business-card/logo-business-card-hero_600-899.webp',
                    '/images/product-landing/hero/logo-business-card/logo-business-card-hero_900-1299.webp',
                    '/images/product-landing/hero/logo-business-card/logo-business-card-hero_1300-above.webp',
                ],
                altText: '', // Display as presentational
                attributions: {
                    id: '5041158',
                    name: 'OscarBastidas',
                },
            },
            why99dTitle: __('So, why 99designs for a business card and logo?'),
            why99dDesignerInfo: {
                id: '5041158',
                name: 'OscarBastidas',
                image: '/images/product-landing/why99d/Why99d_logo-business-card-design.png',
            },
            howItWorksPricing: '/logo-business-card-design/details',
            howItWorksHeading: __('How to get a logo and business card on 99designs'),
            howItWorksContestDescription: __(
                'Get multiple design concepts from our designer community, provide feedback and then pick your favorite design.'
            ),
            howItWorksPricingOption: __('See logo & business card pricing options.'),
            faqHeading: __('Your logo & business card design FAQs, answered.'),
            faqQuestions: [
                {
                    id: '',
                    title: __('What designs will I receive with my logo & business card contest?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                "You'll get a custom logo with a professional business card to show off your new brand!"
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __(
                        'What do I need to prepare for my logo and business card design contest?'
                    ),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'Lots of ideas! If you’re looking for inspiration, <Explore99designs>explore 99designs</Explore99designs> for fantastic examples of logos and business cards created by our design community.'
                            ),
                            links: {
                                Explore99designs: '/discover',
                            },
                        },
                        {
                            copy: __(
                                'Bring as many specifics as you can, too. Include brand details (e.g. colors, fonts), preferred <TypeOfLogo>type of logo</TypeOfLogo>, style, card dimensions, designs you like, brand details, color choices, embossing and foiling requirements, printing specifications, product photos and any other specifications that would affect your design.'
                            ),
                            links: {
                                TypeOfLogo: 'blog/tips/types-of-logos/',
                            },
                        },
                        {
                            copy: __(
                                'The more you give the designers, the better they can design logos and business cards for you!'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __(
                        'What if I have more than one staff member or need additional sizes/types?'
                    ),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'Your custom logo and business card design contest includes one winning logo and one winning card design. If you need a second card for another employee or a different size card, you have a couple of options: you can run a second contest, or you can start a 1-to-1 Project with your winning designer to create the additional versions.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('How can I get my business card printed?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'It’s easy! Once you’ve chosen a winning logo & business card design, you’ll receive ready-to-go files to send to a printer.'
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                'As part of the Vista family, we recommend <Vistaprint>VistaPrint</Vistaprint> for all your printing needs — shocking, we know. They’ve printed billions of business cards (literally), so they know a thing or two about fast and easy service. Of course, if you want to use your own favorite printer, you can do that, too!'
                            ),
                            links: {
                                Vistaprint: 'https://www.vistaprint.com',
                            },
                        },
                    ],
                },
                {
                    id: '',
                    title: __('Who owns what copyright and when?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'On 99designs, designers agree to upload only original artwork, so each designer owns the rights to their design submissions until you choose your contest’s winner. During the design handover stage, the designer agrees to transfer the rights, and you become the owner of the copyright of your winning logo and business card design.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('How can I protect my design contest with an NDA?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'Choose “Make your contest private” from the Contest options section before you launch your logo and business card design contest. This will prohibit designers from using their designs elsewhere, and your contest will be unsearchable on the web.'
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                'This will automatically attach a standard NDA template to your contest. If you prefer, you can also upload your own NDA.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __(
                        'What file types will I receive at the end of my logo & business card design contest?'
                    ),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'In terms of files, you’ll get everything you need to move to production. This means you’ll get the original, editable vector file (usually an Adobe Illustrator file), vector EPS files in both CMYK and RGB (for print and web, respectively), and a web preview file, like a JPG on PNG, so you can preview your logo on virtually any device.'
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                "Plus, you'll get the full legal copyright, so the design will be all yours."
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('Can I use images in my design?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'Yes. If you have any specific images that need to be included with logo or business card design, please submit them to designers with your brief.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('Why do I only see logos in my contest?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'To keep the decision-making process as simple as possible, your contest will focus on the logo portion only. Once you’ve picked a winning logo design to keep, that designer will work with you to create the perfect business card. This usually takes about a week.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('Can I work with my designer once my contest ends?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'Absolutely (and we love it when that happens)! When your Logo & Business Card contest has finished, you can start a <ProjectsLink>1-to-1 Project</ProjectsLink> with your winning designer.'
                            ),
                            links: {
                                ProjectsLink: '/projects',
                            },
                        },
                    ],
                },
                {
                    id: '',
                    title: __(
                        'Which package should I choose for my custom logo and business card contest?'
                    ),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'We guarantee that you’ll get a great logo and business card design at any pricing level, but our higher-priced packages give you more design options and better support.'
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                "Here’s how it works: When you pay more, the prize for the winning designer is bigger, too. More experienced designers participate in your contest, so you get more high-quality designs to choose from. More options means additional time that's needed to review your designs. That’s why our Gold and Platinum packages offer dedicated managers to help streamline that process by writing creative briefs and giving your designers feedback."
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                "When selecting your logo and business card contest package, think about how much time you’ll have and what experience you have writing briefs and working with designers. If you're still unsure, contact us for a design consultation."
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('Is there really a 100% money back guarantee?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'We offer a <MoneyBackGuaranteeLink>100% money-back guarantee</MoneyBackGuaranteeLink> within 60 days of payment on all Logo and Business Card Design Contests, except those with a guaranteed prize and those that have entered the Final Round. You can also call us anytime to help you rewrite your brief, reopen your project to ensure you get a good result, and help you pick a winning design. Remember, if we refund your contest, you aren’t legally entitled to use any of the designs submitted to your contest.'
                            ),
                            links: {
                                MoneyBackGuaranteeLink:
                                    'http://support.99designs.com/hc/en/articles/204108729-100-Money-back-guarantee-For-real',
                            },
                        },
                    ],
                },
            ],
            breadcrumbLabel: __placeholder('Logo & business card'),
        },
        'social-media-pack': {
            title: __('Social Media Pack - Social Media & Graphic Design Packages'),
            description: __(
                'Get a custom social media pack and logo design from the creative experts. Social media branding package and logo design. Award-winning designers. Original designs.'
            ),
            heroTitle: __('Get a social media pack and logo design for your business'),
            productSchema: {
                categoryKey: 'social-media-pack',
                productName: __placeholder('Social Media Pack'),
                description: __placeholder(
                    "Welcome to 99designs, the #1 trusted source for quality design you'll love. Get your perfect logo design with our expert designers today."
                ),
                ratingValue: '4.8',
                reviewCount: 49,
            },
            productDetailsRelativeLink: '/social-media-pack/details',
            heroCtaAppearance: getCtaColorConfig('#F2B62A', '#F2AA00', '#F2B62A', '#1C1B1A'),
            introduction: __(
                'Connect with our creative experts and grow your business with a custom social media pack and logo design.'
            ),
            features: [
                __('1 custom logo design'),
                __('1 Facebook cover'),
                __('1 Twitter header'),
                __('1 Youtube background'),
                __('Editable, digital, and print-ready files'),
                __('Full copyright ownership'),
            ],
            heroImage: {
                sources: [
                    '/images/product-landing/hero/logo-social-media/logo-social-media-pack-hero_0-599.webp',
                    '/images/product-landing/hero/logo-social-media/logo-social-media-pack-hero_600-899.webp',
                    '/images/product-landing/hero/logo-social-media/logo-social-media-pack-hero_900-1299.webp',
                    '/images/product-landing/hero/logo-social-media/logo-social-media-pack-hero_1300-above.webp',
                ],
                altText: '', // Display as presentational
                attributions: {
                    id: '2585628',
                    name: 'Teri-design',
                },
            },
            why99dTitle: __('So, why 99designs for social media pack design?'),
            why99dDesignerInfo: {
                id: '2585628',
                name: 'Teri-design',
                image: '/images/product-landing/why99d/Why99d_social-media-pack.png',
            },
            howItWorksPricing: 'social-media-pack/details',
            howItWorksHeading: __('How to get a social media pack and logo on 99designs'),
            howItWorksContestDescription: __(
                'Get multiple design concepts from our designer community, provide feedback and then pick your favorite design.'
            ),
            howItWorksPricingOption: __('See logo & social media pack pricing options.'),

            faqHeading: __('Your social media pack and logo design FAQs, answered.'),
            faqQuestions: [
                {
                    id: '',
                    title: __(
                        'What designs will I receive with my logo & social media pack contest?'
                    ),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'The name says it all: you’ll get both a custom logo and a professional social media graphics for Facebook, Twitter and YouTube.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __(
                        'What do I need to prepare for my logo and social media design contest?'
                    ),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'Lots of ideas! If you’re looking for inspiration, <Explore99designs>explore 99designs</Explore99designs> for fantastic examples of logos and social media packages created by our design community.'
                            ),
                            links: {
                                Explore99designs: '/discover',
                            },
                        },
                        {
                            copy: __(
                                'Bring as many specifics as you can, too. Include brand details (e.g. colors, fonts), preferred <TypeOfLogo>type of logo</TypeOfLogo>, style suggestions, designs you like, information about your business, product photos and any other specifications that would affect your design.'
                            ),
                            links: {
                                TypeOfLogo: 'blog/tips/types-of-logos/',
                            },
                        },
                        {
                            copy: __(
                                'The more you give the designers, the better they can design logos and business cards for you!'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __(
                        'What if I need additional logo sizes/types or other graphics for more social media networks?'
                    ),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'Your custom logo and social media design contest includes one winning logo and cover images for Facebook, Twitter and YouTube. If you need a second logo for different business or graphics for more social media networks, you have a couple of options: you can run a second contest, or you can start a 1-to-1 Project with your winning designer to create the additional versions.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('Why do I only see logos in my contest?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'To keep the decision-making process as simple as possible, your Logo & social media pack contest will focus on the logo portion only. Once you’ve picked a winning logo design to keep, that designer will work with you to create the perfect Facebook cover, Twitter header and YouTube background. This process usually takes about a week.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('Who owns what copyright and when?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'On 99designs, designers agree to upload only original artwork, so each designer owns the rights to their design submissions until you choose your contest’s winner. During the design handover stage, the designer agrees to transfer the rights, and you become the owner of the copyright of your winning logo and social media images.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('How can I protect my design contest with an NDA?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'Choose “Make your contest private” from the Contest options section before you launch your logo and social media design contest. This will prohibit designers from using their designs elsewhere, and your contest will be unsearchable on the web.'
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                'This will automatically attach a standard NDA template to your contest. If you prefer, you can also upload your own NDA.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __(
                        'What file types will I get with my logo & social media design contest?'
                    ),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                "In terms of files, you'll get everything for both print and digital. This means you'll get the original, editable vector file (usually an Adobe Illustrator file), vector EPS files in both CMYK and RGB (for print and web, respectively), plus digital files (like a JPG or PNG) that you can upload straight to your social media accounts."
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                "Plus, you'll get the full legal copyright, so the design will be all yours."
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('Can I use images in my design?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'Yes. If you have any specific images that need to be included with your logo or social media graphics, please submit them to designers with your brief.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('What dimensions do my Social media designs need to be?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'Our talented designers are experts so they know what size your Facebook cover, Twitter header & YouTube backgrounds needs to be.'
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __(
                        'Once my contest is over, can I continue to work with the winning designer?'
                    ),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'Of course! We actually encourage you do so. You can use our <ProjectsLink>1-1 projects</ProjectsLink> product to continue to work with your winning designer on other design projects (for an additional cost).'
                            ),
                            links: {
                                ProjectsLink: '/projects',
                            },
                        },
                    ],
                },
                {
                    id: '',
                    title: __(
                        'Which package should I choose for my custom logo and social media contest?'
                    ),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'We guarantee that you’ll get a great logo and social media design at any pricing level, but our higher-priced packages give you more design options and better support.'
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                "Here’s how it works: When you pay more, the prize for the winning designer is bigger, too. More experienced designers participate in your contest, so you get more high-quality designs to choose from. More options means additional time that's needed to review your designs. That’s why our Gold and Platinum packages offer dedicated managers to help streamline that process by writing creative briefs and giving your designers feedback."
                            ),
                            links: {},
                        },
                        {
                            copy: __(
                                "When selecting your logo and social media contest package, think about how much time you’ll have and what experience you have writing briefs and working with designers. If you're still unsure, contact us for a design consultation."
                            ),
                            links: {},
                        },
                    ],
                },
                {
                    id: '',
                    title: __('Is there really a 100% money back guarantee?'),
                    keywords: [],
                    messages: [
                        {
                            copy: __(
                                'We offer a <MoneyBackGuaranteeLink>100% money-back guarantee</MoneyBackGuaranteeLink> within 60 days of payment on all Logo and Social Media Design Contests, except those with a guaranteed prize and those that have entered the Final Round. You can also call us anytime to help you rewrite your brief, reopen your project to ensure you get a good result, and help you pick a winning design. Remember, if we refund your contest, you aren’t legally entitled to use any of the designs submitted to your contest.'
                            ),
                            links: {
                                MoneyBackGuaranteeLink:
                                    'http://support.99designs.com/hc/en/articles/204108729-100-Money-back-guarantee-For-real',
                            },
                        },
                    ],
                },
            ],
            breadcrumbLabel: __placeholder('Logo & social media pack'),
        },
    };

    return productLandingGroups[productLandingGroupKey];
};
